// @flow
import * as React from 'react'
import styled from 'styled-components'
import SubscriptionName from '../../SubscriptionName'
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js'
import type { SubscriptionLengthType } from '../../../../flowTypes'
import { FadeInImage } from '@components/shared/FadeInImage'
import { CardBottomRowItem2, CardNumberContainer2, colors, CTASecondaryWhite, DipseaContentfulAssets, ErrorBodyText, Form } from '@components/shared'
import CheckIcon from '@components/shared/icons/CheckIcon'
import { config } from 'react-awesome-styled-grid'
import { defaultStripeStyles } from '@components/subscribe/SubscriptionModal/Paywall/Paywall'
import PaymentRequestButton from '@components/subscribe/SubscriptionModal/Paywall/PaymentRequestButton'

export type PropsType = {
  inProcess: boolean,
  isSubscribed: boolean,
  userLoaded: boolean,
  plan?: SubscriptionLengthType,
  price: string,
  error?: string,
  subtype: "monthly" | "annual",
  subscriptionOfferCta: string,
  subscriptionOfferTitle: string,
  subscriptionOfferSubtitle: string,
  subscriptionOfferSubtext: string,
  isBlackFriday?: boolean,
  pageSlugForPromoEvent?: string,
  existingPromoCode?: string,
  isSocialPayButtonsFirst: boolean,
  trialDays: number
}

export type DispatchPropsType = {
  applyPromo: (promo: string, isMarketingLandingPage: boolean) => void,
  clearPromo: () => void,
  setPlan: (plan: "monthly" | "annual") => void,
  subscribe: (
    stripeObj: any,
    cardNumberElement?: any,
    altPaymentEvent?: any
  ) => void
}

export default function PromoSubscriptionForm (
  props: PropsType & DispatchPropsType
): React.Node {
  const [formattedError, setFormattedError] = React.useState<?string>()
  const [submitted, setSubmitted] = React.useState(false)
  const elements = useElements()
  const stripe = useStripe()
  // on mount set to annual, on unmount unset
  React.useEffect((): any => {
    if (props.subtype === 'annual' || props.subtype === 'monthly') {
      props.setPlan(props.subtype)
    } else {
      // default to annual
      props.setPlan('annual')
    }

    let code = 'hunk-quiz-week-free'

    if (props.existingPromoCode) {
      // props.existingPromoCode must be set in helpers/promos.js
      code = props.existingPromoCode
    } else if (props.pageSlugForPromoEvent) {
      code = props.pageSlugForPromoEvent
    } else if (props.isBlackFriday) {
      code = 'blackfriday50-1'
    }

    props.applyPromo(code, !!props.pageSlugForPromoEvent)
    return (): void => {
      if (!props.existingPromoCode) {
        props.clearPromo()
      }
    }
  }, [])

  React.useEffect((): void => {
    if (props.error) {
      if (props.error && props.error.length > 70) {
        setFormattedError(
          'There was an error. Please try reloading and try again.'
        )
      } else {
        setFormattedError(props.error)
      }
    }
    if (submitted && props.error) {
      setSubmitted(false)
    }
  }, [props.error])

  const submit =
    (e: any): void => {
      e.preventDefault()

      const cardNumberElement = elements.getElement(CardNumberElement)
      if (submitted) {
        return
      }
      setSubmitted(true)
      props.subscribe(stripe, cardNumberElement, null)
    }

  const altPaymentSubmit = (altPaymentEvent: any): void => {
    props.subscribe(stripe, null, altPaymentEvent)
  }

  const socialPayButton = (): React.Node => {
    return (
      <PaymentRequestButton
        stripe={stripe}
        price={props.price}
        plan={props.plan}
        trialDays={props.trialDays}
        subscribe={(altPaymentEvent: any): void =>
          altPaymentSubmit(altPaymentEvent)
        }
        isPrimaryWhite={props.isSocialPayButtonsFirst}
      />
    )
  }

  return (
    <NewContainer id='promo-subscription-form'>
      <BackgroundImageContainer isFullScreen={false}>
        <FadeInImage
          src={DipseaContentfulAssets.auth_modal_gradient}
          objectFit="cover"
          layout="fill"
          fadeInTime={0.3}
        />
      </BackgroundImageContainer>

      <SubscriptionModalContainer>
        <Header
          dangerouslySetInnerHTML={{ __html: props.subscriptionOfferTitle }}
        />
        <SubHeaderContainer>
          {props.isBlackFriday
            ? (
            <LongSubText>
              Annual subscription of $34.99 <s>$69.99</s> USD billed at trial
              end
            </LongSubText>
              )
            : (
            <LongSubText
              dangerouslySetInnerHTML={{ __html: props.subscriptionOfferSubtitle }}
            />
              )}
          <ValueProps />
        </SubHeaderContainer>
        <FormWrapper>
          {props.isSocialPayButtonsFirst && (
            socialPayButton()
          )}
          <PaymentDivider />
          <Form padding="0">
            <CardFormContainer>
              <CardInputContainer>
                <SectionTitle>Credit Card</SectionTitle>
                <SubscriptionNameContainer>
                  <SubscriptionName isPromo={true}/>
                </SubscriptionNameContainer>
                <CardNumberContainer2>
                  <CardNumberElement options={defaultStripeStyles} />
                </CardNumberContainer2>
                <CardBottomRow>
                  <CardBottomRowItem2 margin={'0 7.5px 0 0'}>
                    <CardExpiryElement options={defaultStripeStyles} />
                  </CardBottomRowItem2>
                  <CardBottomRowItem2 margin={'0 0 0 7.5px'}>
                    <CardCvcElement options={defaultStripeStyles} />
                  </CardBottomRowItem2>
                </CardBottomRow>
                <ErrorContainer>
                  <ErrorBodyText showError={!!formattedError}>
                    {formattedError}
                  </ErrorBodyText>
                </ErrorContainer>
              </CardInputContainer>
              <SubscribeButtonContainer>
                <CTASecondaryWhite
                  className="tc-checkout-submit"
                  type="submit"
                  onClick={submit}
                  disabled={submitted}
                >
                  {props.inProcess ? 'Processing' : props.subscriptionOfferCta}
                </CTASecondaryWhite>

                {!props.isSocialPayButtonsFirst && (
                  socialPayButton()
                )}

              </SubscribeButtonContainer>
              <TermsLinks />
            </CardFormContainer>
            <PaymentTerms>{props.subscriptionOfferSubtext}</PaymentTerms>
          </Form>
        </FormWrapper>
      </SubscriptionModalContainer>
    </NewContainer>
  )
}

const Header = styled.div`
  color: ${colors.white};
  font-family: "Queens Condensed Light";
  font-style: normal;
  font-weight: normal;
  font-size: 36px;
  line-height: 110%;
  margin-block: 0;
  padding: 0;
  margin: 0 20px 12px;
  text-align: center;
`
const LongSubText = styled.p`
  color: ${colors.white};
  font-family: "Good Sans Regular";
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 150%;
  margin-block: 0;
  padding: 0 30px;
  margin: 0 auto 12px;
  text-align: center;
`
export const SubContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding: 40px 0px;
`
const NewContainer = styled(SubContainer)`
  padding: 70px 0px;
`
const BackgroundImageContainer = styled.div`
  position: fixed;
  z-index: -1;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  ${(props: any): string => config(props).media.md`
    position: ${(props: { isFullScreen: boolean }): string =>
      props.isFullScreen ? 'fixed' : 'absolute'};
  `};
`

export const SubscriptionModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`
export const IconContainer = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  margin: 15px auto;
  max-height: 75px;
  ${(props: any): string => config(props).media.md`
    margin: 20px auto;
  `}
`
export const SubHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
`
export const FormWrapper = styled.div`
  align-self: center;
  display: flex;
  flex-direction: column;
  max-width: 415px;
  margin: 0 20px;
  ${(props: any): string => config(props).media.md`
    width: 100%;
  `}
`
export const ErrorContainer = styled.div`
  bottom: -1.25em;
  left: 0;
  max-height: 20px;
  position: absolute;
`
export const SectionTitle = styled.h4`
  font-family: 'Good Sans Bold';
  font-style: normal;
  font-weight: bold;
  font-size: 14px;
  line-height: 150%;
  margin: 0 0 22px;
  ${(props: any): string => config(props).media.md`
    font-size: 16px;
  `}
`
export const SubHeaderBold = styled.div`
  font-family: 'Good Sans Light';
  //letter-spacing: 0.2px;
  text-align: center;
  font-style: normal;
  font-size: 16px;
  line-height: 22px;
  ${(props: any): string => config(props).media.md`
    font-size: 16px;
    line-height: 22px;
  `}
`
export const SubHeader = styled.div`
  font-family: 'Good Sans Light';
  letter-spacing: 0.2px;
  margin: 0 auto 10px;
  text-align: center;
  font-style: normal;
  font-size: 14px;
  line-height: 18px;
  ${(props: any): string => config(props).media.md`
    margin: 0 auto 10px;
    font-size: 14px;
    line-height: 18px;
  `}
`
export const PaymentTerms = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  font-family: 'Good Sans Light';
  font-size: 10px;
  font-style: normal;
  font-weight: normal;
  line-height: 150%;
  padding: 0;
  text-align: center;
  width: 100%;
  color: #FFFFFF;
`
export const SubscribeButtonContainer = styled.div`
  flex:1;
  padding: 8px 0 0 0;
`
export const SubscriptionNameContainer = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  input {
    border: 1px solid ${colors.white70} !important;
    background: transparent !important;
    color: ${colors.white} !important;
    &:focus {
      border: 1px solid ${colors.white70} !important;
      background: transparent !important;
    }
    &::placeholder {
      color: ${colors.white70} !important;
    }
    &--webkit-autofill {
      background-color: ${colors.white25} !important;
    }
  }
  ::placeholder {
    color: rgba(246, 243, 240, 0.5);
  }
`
export const CardInputContainer = styled.div`
  display: flex;
  flex: 2;
  flex-direction: column;
  margin: 0 0 15px 0;
  position: relative;
  ${(props: any): string => config(props).media.xl`
    margin: 0 0 20px 0;
  `}
`
export const CardFormContainer = styled.div`
  display: flex;
  flex-direction: column;
`
export const CardBottomRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`
export const PaymentDivider = styled.div`
  border-bottom: 0.5px solid ${colors.white25};
  margin: 12px 0 16px;
  ${(props: any): string => config(props).media.md`
    margin: 20px 0 24px;
  `}
`
export const TermsContainer = styled.div`
  font-family: 'Good Sans Light';
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 150%;
  padding: 25px 10px 16px;
  text-align: center;
  a {
    text-decoration: underline;
    color: ${colors.white};
    margin: 0;
    :hover {
      opacity: 0.5;
    }
    outline: none;
    :focus {
      box-shadow: 0 0 0 1pt ${colors.white};
      border-radius: 5px;
    }
  }
`
export const ValueProps = (): React.Node => {
  return (
    <ValuePropContainer>
      <div>
        <CheckIcon color={colors.white70} />
        <p>Cancel Anytime</p>
      </div>
      <div>
        <CheckIcon color={colors.white70} />
        <p>Ad Free, Always</p>
      </div>
      <div>
        <CheckIcon color={colors.white70} />
        <p>Hundreds of Stories</p>
      </div>
    </ValuePropContainer>
  )
}
const ValuePropContainer = styled.div`
  align-self: center;
  display:flex;
  flex-wrap: wrap;
  justify-content:center;
  margin: 0;
  div {
    align-items:center;
    display:flex;
    padding: 4px 8px;
  
    :first-child {
      padding-left: 0;
    }
      :last-child {
      padding-right: 0;
    }
  }
  p {
    color: ${colors.white70};
    font-family: 'Good Sans Light';
    font-size: 12px;
    font-style: normal;
    font-weight: normal;
    /* letter-spacing: 0.173333px; */
    line-height: 1em;
    margin: 0;
    padding: 0 0 0 5px;
  }

  ${(props: any): string => config(props).media.md`
    max-width: 100%;
    font-size: 100px;
  `};
`
export const TermsLinks = (): React.Node => {
  return (
    <TermsContainer>
      <a target="_blank" rel="noopener noreferrer" href="https://www.dipseastories.com/terms/dark/">
        Terms & Conditions
      </a>
      {' • '}
      <a target="_blank" rel="noopener noreferrer" href="https://www.dipseastories.com/privacypolicy/dark">
        Privacy
      </a>
      {' • '}
      <a target="_blank" rel="noopener noreferrer" href="https://www.dipseastories.com/paymentterms/dark/">
        Payment Terms
      </a>
    </TermsContainer>
  )
}
